import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { deepMerge } from '@/api';
import { areaResources } from './areas';
import { getRouteLanguage, redirectFromBaseURL } from './languageDetection';
// Import docs translation files
import arDocs from './locales/ar-docs.json';
// Import integrations translation files
import arIntegrations from './locales/ar-integrations.json';
// Strings shared with the absorbed component library (glob_navigation,
// glob_footer, auth screens, ...). Merged first so app files win on collision.
import arShared from './locales/ar-shared.json';
import arSharedIntegrations from './locales/ar-shared-integrations.json';
// Import formshive-specific translation files
import arUnique from './locales/ar-unique.json';
import deDocs from './locales/de-docs.json';
import deIntegrations from './locales/de-integrations.json';
import deShared from './locales/de-shared.json';
import deSharedIntegrations from './locales/de-shared-integrations.json';
import deUnique from './locales/de-unique.json';
import enDocs from './locales/en-docs.json';
import enIntegrations from './locales/en-integrations.json';
import enShared from './locales/en-shared.json';
import enSharedIntegrations from './locales/en-shared-integrations.json';
import enUnique from './locales/en-unique.json';
import esDocs from './locales/es-docs.json';
import esIntegrations from './locales/es-integrations.json';
import esShared from './locales/es-shared.json';
import esSharedIntegrations from './locales/es-shared-integrations.json';
import esUnique from './locales/es-unique.json';
import frDocs from './locales/fr-docs.json';
import frIntegrations from './locales/fr-integrations.json';
import frShared from './locales/fr-shared.json';
import frSharedIntegrations from './locales/fr-shared-integrations.json';
import frUnique from './locales/fr-unique.json';
import ptDocs from './locales/pt-docs.json';
import ptIntegrations from './locales/pt-integrations.json';
import ptShared from './locales/pt-shared.json';
import ptSharedIntegrations from './locales/pt-shared-integrations.json';
import ptUnique from './locales/pt-unique.json';
import thDocs from './locales/th-docs.json';
import thIntegrations from './locales/th-integrations.json';
import thShared from './locales/th-shared.json';
import thSharedIntegrations from './locales/th-shared-integrations.json';
import thUnique from './locales/th-unique.json';
import zhDocs from './locales/zh-docs.json';
import zhIntegrations from './locales/zh-integrations.json';
import zhShared from './locales/zh-shared.json';
import zhSharedIntegrations from './locales/zh-shared-integrations.json';
import zhUnique from './locales/zh-unique.json';

// One i18next instance for the whole app, including the absorbed shared
// components. The `*-shared` files come first in each chain so an app string
// overrides a shared one of the same key; the per-area files come last.
//
// IMPORTANT: use deepMerge, not spread. Both `<lang>-integrations.json` and
// `<lang>-shared-integrations.json` write a top-level `integrations` object, and a
// shallow spread would replace one subtree wholesale rather than merging, so
// the UI would render raw `integrations.form.*` keys.
const resources = {
  en: {
    translation: deepMerge(
      enShared,
      enSharedIntegrations,
      enUnique,
      enDocs,
      enIntegrations,
      areaResources('en')
    ),
  },
  de: {
    translation: deepMerge(
      deShared,
      deSharedIntegrations,
      deUnique,
      deDocs,
      deIntegrations,
      areaResources('de')
    ),
  },
  es: {
    translation: deepMerge(
      esShared,
      esSharedIntegrations,
      esUnique,
      esDocs,
      esIntegrations,
      areaResources('es')
    ),
  },
  fr: {
    translation: deepMerge(
      frShared,
      frSharedIntegrations,
      frUnique,
      frDocs,
      frIntegrations,
      areaResources('fr')
    ),
  },
  pt: {
    translation: deepMerge(
      ptShared,
      ptSharedIntegrations,
      ptUnique,
      ptDocs,
      ptIntegrations,
      areaResources('pt')
    ),
  },
  zh: {
    translation: deepMerge(
      zhShared,
      zhSharedIntegrations,
      zhUnique,
      zhDocs,
      zhIntegrations,
      areaResources('zh')
    ),
  },
  th: {
    translation: deepMerge(
      thShared,
      thSharedIntegrations,
      thUnique,
      thDocs,
      thIntegrations,
      areaResources('th')
    ),
  },
  ar: {
    translation: deepMerge(
      arShared,
      arSharedIntegrations,
      arUnique,
      arDocs,
      arIntegrations,
      areaResources('ar')
    ),
  },
};

// Turn a stored/browser preference into a URL prefix before anything renders,
// so the router and i18n below both read the same, final hash.
if (typeof window !== 'undefined') {
  redirectFromBaseURL();
}

// Initialize i18n instance for formshive
// Apps don't use LanguageDetector - they use synchronous init + detectAndSetLanguage in App.tsx.
// `lng` comes from the route, not from storage: LanguageRoutes derives the
// language from the same hash prefix, and seeding a different one here makes
// the two fight on the first render.
export const i18nInitPromise = i18n.use(initReactI18next).init({
  resources,
  lng: typeof window !== 'undefined' ? getRouteLanguage() : 'en',
  fallbackLng: 'en',
  defaultNS: 'translation',
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;
